/**
 * SendToEstimateDialog — pick this job's PDFs (CAD sheets render as PDF),
 * name the new AI-estimate project, create it with the files copied in,
 * then jump to the Estimate tab opened on that project.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Overlay, DialogTitle } from "./dialogs";
import { sendToEstimate } from "./api";
import { isPdf, INPUT, LABEL, BTN_ORANGE, BTN_GHOST } from "./theme";

const asPdf = (d) => isPdf(d) || (d?.cad && d.cad.status === "ready");

export default function SendToEstimateDialog({ open, onClose, project, drawings, preselect = [] }) {
  const pdfs = useMemo(() => (drawings || []).filter(asPdf), [drawings]);
  const [picked, setPicked] = useState(new Set());
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(new Set(preselect.length ? preselect : pdfs.map((d) => d.drawing_id)));
    setName(project?.name || "");
    setLocation(project?.address || project?.location || "");
    setBusy(false);
  }, [open]); // eslint-disable-line

  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Give the estimate project a name"); return; }
    if (!picked.size) { toast.error("Pick at least one PDF"); return; }
    setBusy(true);
    try {
      const r = await sendToEstimate(project.project_id, { name: name.trim(), location, drawing_ids: [...picked] });
      toast.success(`Created "${r.name}" with ${r.copied.length} file${r.copied.length === 1 ? "" : "s"} — opening the Estimate tab`);
      onClose?.();
      // Dashboard listens: switches to the Estimate tab (AI version) and
      // the estimating module navigates to the new project's workspace.
      sessionStorage.setItem("est_open_project", r.project_id);
      window.dispatchEvent(new CustomEvent("elite:open-ai-estimate", { detail: { projectId: r.project_id } }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create the estimate project");
    } finally { setBusy(false); }
  };

  return (
    <Overlay open={open} onClose={onClose}>
      <form onSubmit={submit} data-testid="send-to-estimate-dialog">
        <DialogTitle onClose={onClose}>Send to Estimate</DialogTitle>
        <p className="mb-3 text-xs text-slate-500">Creates a project in the AI estimating section and copies the selected PDFs into it.</p>
        <label className={LABEL}>Project name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT} placeholder="e.g. 893 & 911 Lockhart Subdivision" data-testid="send-estimate-name" />
        <label className={`${LABEL} mt-3`}>Location (optional)</label>
        <input value={location} onChange={(e) => setLocation(e.target.value)} className={INPUT} placeholder="Town / site" data-testid="send-estimate-location" />
        <div className="mt-3 flex items-center justify-between">
          <label className={LABEL}>PDF files ({picked.size}/{pdfs.length})</label>
          <div className="flex gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <button type="button" onClick={() => setPicked(new Set(pdfs.map((d) => d.drawing_id)))} data-testid="send-estimate-all">All</button>
            <button type="button" onClick={() => setPicked(new Set())} data-testid="send-estimate-none">None</button>
          </div>
        </div>
        <ul className="mt-1 max-h-64 overflow-y-auto rounded-lg border border-slate-200" data-testid="send-estimate-files">
          {pdfs.map((d) => (
            <li key={d.drawing_id} className="flex items-center gap-2 border-b border-slate-100 px-3 py-1.5 text-xs last:border-0">
              <input type="checkbox" checked={picked.has(d.drawing_id)} onChange={() => toggle(d.drawing_id)} data-testid={`send-estimate-file-${d.drawing_id}`} />
              <span className="min-w-0 flex-1 truncate" title={d.name}>{d.name}</span>
              {d.cad && <span className="rounded bg-blue-50 px-1 text-[9px] font-semibold uppercase text-[#1a3fd1]">CAD</span>}
              {d.pages > 1 && <span className="text-[10px] text-slate-400">{d.pages} pp</span>}
            </li>
          ))}
          {pdfs.length === 0 && <li className="p-4 text-center text-xs text-slate-400">No PDF files in this job yet.</li>}
        </ul>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={BTN_GHOST} data-testid="send-estimate-cancel">Cancel</button>
          <button type="submit" disabled={busy} className={BTN_ORANGE} data-testid="send-estimate-submit">
            {busy ? <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1 inline h-3.5 w-3.5" />}Create & open estimate
          </button>
        </div>
      </form>
    </Overlay>
  );
}
